import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { SegmentRule, Segment, VoterCounts, VoterDataCategoryDto, VoterDataFieldDto } from '@signalvote/shared';
import { VoterDataFieldKind } from '@signalvote/shared';
import { useVoterRuleOptions, useVoterVoteCounts } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VoterCount } from './VoterCount';
import { CheckBox, FlagOptions, ModeledTag, VoteOptions } from './VoterDataFieldPopover';
import { fieldCountsOf, isNarrowed, isYesNo, keptOf, missingPercent, toggledKept, votedOf } from './voterSegment';
import type { FieldCounts } from './voterSegment';

const MOST_USED = 'Most used';

/**
 * "Explore who's in it": a quiet profile of the list, one row per catalog field with its
 * breakdown, grouped into collapsible categories. Rows are grey data until the customer narrows
 * one; only a narrowed row takes the accent colour. Every row edits the same rules as the
 * sentence, with the same filter-state model as the field popover.
 *
 * A vote-history row asks for its counts only when the customer asks, since each costs an L2 call
 * and a group can be open without anyone looking. A narrowed row shows its other options once its
 * popover has loaded them.
 */
export function VoterDataExplore({
  categories,
  segment,
  rules,
  counts,
  provisional,
  capReason,
  onKeep,
  onVoted,
}: {
  categories: VoterDataCategoryDto[];
  segment: Segment;
  rules: SegmentRule[];
  counts: VoterCounts | undefined;
  provisional: boolean;
  /** Why no rule can be added, at L2's filter cap. */
  capReason: string | null;
  onKeep: (field: VoterDataFieldDto, kept: string[]) => void;
  onVoted: (field: VoterDataFieldDto, voted: boolean | null) => void;
}) {
  const [shown, setShown] = useState(true);
  const [openGroups, setOpenGroups] = useState(() => new Set([MOST_USED]));
  const fields = categories.flatMap((c) => c.fields);
  const groups: [string, VoterDataFieldDto[]][] = [
    [MOST_USED, fields.filter((f) => f.common)] as [string, VoterDataFieldDto[]],
    ...categories.map((c): [string, VoterDataFieldDto[]] => [c.label, c.fields.filter((f) => !f.common)]),
  ].filter(([, fs]) => fs.length > 0);

  if (!shown) {
    return (
      <motion.div layout="position">
        <Button variant="ghost" className="mt-6 pl-0 text-primary hover:bg-transparent" onClick={() => setShown(true)}>
          Explore all {fields.length} fields in this list <span aria-hidden="true">↓</span>
        </Button>
      </motion.div>
    );
  }

  const toggleGroup = (name: string) =>
    setOpenGroups((current) => {
      const next = new Set(current);
      if (!next.delete(name)) next.add(name);
      return next;
    });

  return (
    <motion.section
      layout="position"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      aria-labelledby="voter-explore-heading"
      className="mt-10"
    >
      <div className="mb-2.5 flex items-center justify-between">
        <h3 id="voter-explore-heading" className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">
          Explore who’s in it
        </h3>
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setShown(false)}>
          Hide
        </Button>
      </div>
      <div className="border-t">
        {groups.map(([name, groupFields]) => {
          const open = openGroups.has(name);
          const inUse = groupFields.filter((f) => isNarrowed(f, rules)).length;
          return (
            <div key={name} className="border-b">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggleGroup(name)}
                className="flex min-h-12 w-full items-center gap-2.5 px-1 text-left text-sm font-medium text-foreground/80 active:scale-[0.99] motion-reduce:transform-none"
              >
                {name}
                <span className="text-xs font-normal text-muted-foreground">
                  {groupFields.length} field{groupFields.length === 1 ? '' : 's'}
                </span>
                {inUse > 0 && <span className="text-xs font-semibold text-primary">{inUse} in use</span>}
                <ChevronRight
                  aria-hidden="true"
                  className={cn('ml-auto size-3.5 text-muted-foreground transition-transform motion-reduce:transition-none', open && 'rotate-90')}
                />
              </button>
              {open && (
                // Grows open, so the categories below slide down rather than jump.
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                  className="overflow-hidden"
                >
                  {groupFields.map((field) => (
                    <ExploreRow
                      key={field.id}
                      field={field}
                      segment={segment}
                      rules={rules}
                      counts={counts}
                      provisional={provisional}
                      capReason={capReason}
                      onKeep={(kept) => onKeep(field, kept)}
                      onVoted={(voted) => onVoted(field, voted)}
                    />
                  ))}
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

function ExploreRow({
  field,
  segment,
  rules,
  counts,
  provisional,
  capReason,
  onKeep,
  onVoted,
}: {
  field: VoterDataFieldDto;
  segment: Segment;
  rules: SegmentRule[];
  counts: VoterCounts | undefined;
  provisional: boolean;
  capReason: string | null;
  onKeep: (kept: string[]) => void;
  onVoted: (voted: boolean | null) => void;
}) {
  const [refused, setRefused] = useState(false);
  const [votesAsked, setVotesAsked] = useState(false);
  const vote = field.kind === VoterDataFieldKind.Vote;
  const narrowed = isNarrowed(field, rules);
  const locked = !narrowed && capReason !== null;
  const kept = keptOf(field, rules);
  const without = useVoterRuleOptions(segment, field.id, false).data;
  const votes = useVoterVoteCounts(segment, field.id, vote && votesAsked).data;
  const fieldCounts = fieldCountsOf(field, rules, counts, without);
  const missing = missingPercent(field, fieldCounts);
  const labelId = `voter-explore-${field.id}`;
  const keep = (next: string[]) => {
    setRefused(false);
    onKeep(next);
  };

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-busy={provisional}
      className="grid gap-2 border-t px-1 py-3 first-of-type:border-t-0 sm:grid-cols-[210px_1fr] sm:gap-4"
    >
      <div className="pt-1.5">
        <span className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium text-foreground/80">
          <span id={labelId}>{field.label}</span>
          {field.modeled && <ModeledTag />}
        </span>
        <span className="mt-0.5 block text-xs font-semibold text-muted-foreground/80">
          {narrowed ? (
            <>
              <span className="text-primary">{isYesNo(field) ? 'Filtering' : `Only ${kept.length} of ${field.options.length}`}</span>
              <button
                type="button"
                onClick={() => (vote ? onVoted(null) : keep(field.options.map((o) => o.label)))}
                className="ml-2 font-semibold text-primary underline decoration-primary/40 underline-offset-2"
              >
                Reset
              </button>
            </>
          ) : (
            (locked && capReason) || 'Everyone'
          )}
        </span>
        {missing !== null && (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {narrowed ? 'Leaves out' : 'Includes'} the {missing}% with no data
          </span>
        )}
        {vote && !votesAsked && votes === undefined && (
          <button
            type="button"
            onClick={() => setVotesAsked(true)}
            className="mt-0.5 block text-xs font-semibold text-primary underline decoration-primary/40 underline-offset-2"
          >
            Show counts
          </button>
        )}
        {refused && (
          <span aria-live="polite" className="mt-1 block text-xs text-muted-foreground">
            Keep at least one checked. Use “only” to pick a single value.
          </span>
        )}
      </div>
      <fieldset disabled={locked} className={cn('min-w-0 disabled:opacity-60', provisional && 'voter-provisional')}>
        {vote ? (
          <VoteOptions field={field} voted={votedOf(field, rules)} base={fieldCounts.base} votes={votes} onVoted={onVoted} />
        ) : isYesNo(field) ? (
          <FlagOptions field={field} kept={kept} narrowed={narrowed} counts={fieldCounts} onKeep={keep} note={false} />
        ) : (
          <EnumTiles field={field} kept={kept} narrowed={narrowed} counts={fieldCounts} onKeep={keep} onRefuse={() => setRefused(true)} />
        )}
      </fieldset>
    </div>
  );
}

function EnumTiles({
  field,
  kept,
  narrowed,
  counts,
  onKeep,
  onRefuse,
}: {
  field: VoterDataFieldDto;
  kept: string[];
  narrowed: boolean;
  counts: FieldCounts;
  onKeep: (kept: string[]) => void;
  onRefuse: () => void;
}) {
  const toggle = (label: string) => {
    const next = toggledKept(kept, label);
    return next ? onKeep(next) : onRefuse();
  };
  const { base } = counts;
  const shares = field.options.map((o) => {
    const n = counts.options[o.label];
    return base && n !== undefined ? n / base : 0;
  });
  const largest = Math.max(...shares, 0);

  return (
    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-[repeat(auto-fill,minmax(136px,1fr))]">
      {field.options.map((option, i) => {
        const on = kept.includes(option.label);
        const accent = narrowed && on;
        return (
          <div key={option.label} className="group relative">
            <button
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(option.label)}
              className={cn(
                'relative grid min-h-[52px] w-full grid-cols-[16px_1fr] items-center gap-x-2 gap-y-px overflow-hidden rounded-md border py-[7px] pl-2.5 pr-9 text-left transition-[transform,background-color,border-color] active:scale-[0.97] motion-reduce:transition-none',
                !on && 'border-dashed border-border bg-transparent',
                on && !accent && 'border-transparent bg-muted hover:border-border',
                accent && 'border-primary/30 bg-primary/10',
              )}
            >
              <span className="row-span-2">
                <CheckBox on={on} accent={narrowed} />
              </span>
              <span className={cn('text-[13px]', accent ? 'font-semibold text-primary' : on ? 'font-medium text-foreground/80' : 'text-muted-foreground')}>
                {option.label}
              </span>
              <span className="col-start-2 text-xs tabular-nums text-muted-foreground">
                <VoterCount value={counts.options[option.label]} />
                {base ? ` · ${Math.round(shares[i] * 100)}%` : ''}
              </span>
              <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px]">
                <span
                  className={cn(
                    'block h-full origin-left transition-transform duration-500 ease-out motion-reduce:transition-none',
                    accent ? 'bg-primary' : 'bg-muted-foreground/30',
                  )}
                  style={{ transform: `scaleX(${largest ? shares[i] / largest : 0})` }}
                />
              </span>
            </button>
            <button
              type="button"
              aria-label={`Only ${option.label}`}
              onClick={() => onKeep([option.label])}
              className="absolute right-1.5 top-[7px] min-h-6 rounded px-1.5 text-[11px] font-bold text-muted-foreground hover:bg-muted hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:focus-visible:opacity-100"
            >
              only
            </button>
          </div>
        );
      })}
    </div>
  );
}
